import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type {
  CreateTag,
  FolderDraft,
  CopyTopic,
  TopicDraft,
  UseTopic,
  UpdateTag,
} from '@headline/schemas'
import { api } from './api'

// ── Report configuration ─────────────────────────────────────────────────────────────────

// ── Topics, folders, tags ─────────────────────────────────────────────────────────────
// All sub-account-scoped: the id rides in the path (`/api/topics/:subaccountId/...`), so every
// query is keyed by it and every mutation invalidates that sub-account's slice. The wizard's
// create/update use `mutateAsync` so a 409 (duplicate name) or 403 (source not entitled) surfaces
// in the wizard instead of a silent navigation.

export function topicsQuery(subaccountId: string) {
  return queryOptions({
    queryKey: ['topics', subaccountId],
    queryFn: async () => {
      const res = await api.api.topics[':subaccountId'].$get({ param: { subaccountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load topics')
      }
      return res.json()
    },
  })
}

export function foldersQuery(subaccountId: string) {
  return queryOptions({
    queryKey: ['folders', subaccountId],
    queryFn: async () => {
      const res = await api.api.folders[':subaccountId'].$get({ param: { subaccountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load folders')
      }
      return res.json()
    },
  })
}

export function tagsQuery(subaccountId: string) {
  return queryOptions({
    queryKey: ['tags', subaccountId],
    queryFn: async () => {
      const res = await api.api.tags[':subaccountId'].$get({ param: { subaccountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load tags')
      }
      return res.json()
    },
  })
}

/** The delete guard's counts. Fetched on demand when a delete is being confirmed, so it's not part
 *  of the tags list payload — hence its own key and a short `gcTime` (the numbers go stale fast). */
export function tagUsageQuery(subaccountId: string, tagId: string) {
  return queryOptions({
    queryKey: ['tags', subaccountId, tagId, 'usage'],
    queryFn: async () => {
      const res = await api.api.tags[':subaccountId'][':tagId'].usage.$get({
        param: { subaccountId, tagId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load tag usage')
      }
      return res.json()
    },
    gcTime: 10_000,
  })
}

// Topics and folders can both re-parent topics (a folder delete orphans its topics), so folder
// mutations invalidate topics too. Tag deletes cascade query/topic mappings, so they do as well.
function invalidateTopics(queryClient: QueryClient, subaccountId: string) {
  return queryClient.invalidateQueries({ queryKey: ['topics', subaccountId] })
}

export function useCreateTopic(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (draft: TopicDraft) => {
      const res = await api.api.topics[':subaccountId'].$post({
        param: { subaccountId },
        json: draft,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create the topic')
      }
      return res.json()
    },
    onSuccess: () => invalidateTopics(queryClient, subaccountId),
  })
}

export function useUpdateTopic(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { topicId: string; draft: TopicDraft }) => {
      const res = await api.api.topics[':subaccountId'][':topicId'].$put({
        param: { subaccountId, topicId: input.topicId },
        json: input.draft,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to save the topic')
      }
      return res.json()
    },
    onSuccess: () => invalidateTopics(queryClient, subaccountId),
  })
}

export function useDeleteTopic(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (topicId: string) => {
      const res = await api.api.topics[':subaccountId'][':topicId'].$delete({
        param: { subaccountId, topicId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to delete the topic')
      }
      return res.json()
    },
    onSuccess: () => invalidateTopics(queryClient, subaccountId),
  })
}

export function useMoveTopic(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { topicId: string; folderId: string | null }) => {
      const res = await api.api.topics[':subaccountId'][':topicId'].move.$post({
        param: { subaccountId, topicId: input.topicId },
        json: { folderId: input.folderId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to move the topic')
      }
      return res.json()
    },
    onSuccess: () => invalidateTopics(queryClient, subaccountId),
  })
}

/**
 * Record that a product started using a topic (CLI-744), for surfaces whose attachments the server
 * can't see — the stream's Configure page keeps them in the browser. Reports and RTAs record it
 * server-side when they attach, so they never call this.
 */
export function useRecordTopicUse(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { subaccountId: string; topicId: string } & UseTopic) => {
      const { subaccountId, topicId, products } = input
      const res = await api.api.topics[':subaccountId'][':topicId']['used-for'].$post({
        param: { subaccountId, topicId },
        json: { products },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to record the topic use')
      }
      return res.json()
    },
    onSuccess: (_topic, input) => invalidateTopics(queryClient, input.subaccountId),
  })
}

export function useCopyTopic(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { subaccountId: string; topicId: string } & CopyTopic) => {
      const { subaccountId, topicId, ...body } = input
      const res = await api.api.topics[':subaccountId'][':topicId'].copy.$post({
        param: { subaccountId, topicId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to copy the topic')
      }
      return res.json()
    },
    // Copies land in OTHER sub-accounts, so refresh every topic list rather than just the origin's.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['topics'] }),
  })
}

/** Every topic across every client in the org, with its owner — the internal Topics Library's list.
 *  Staff-only at the API (`manage-all:accounts`). */
export function topicLibraryQuery() {
  return queryOptions({
    queryKey: ['topic-library'],
    queryFn: async () => {
      const res = await api.api.topics.library.$get()
      if (!res.ok) {
        const body = (await res.json()) as { error?: { message?: string } }
        throw new Error(body.error?.message ?? 'Failed to load topics')
      }
      return res.json()
    },
  })
}

/** Every sub-account in the org — the Topics Library copy picker's targets (a client, or an
 *  account's own space). Staff-only. */
export function topicLibraryClientsQuery() {
  return queryOptions({
    queryKey: ['topic-library', 'clients'],
    queryFn: async () => {
      const res = await api.api.topics.library.clients.$get()
      if (!res.ok) {
        const body = (await res.json()) as { error?: { message?: string } }
        throw new Error(body.error?.message ?? 'Failed to load clients')
      }
      return res.json()
    },
  })
}

/** Copy a topic from one client into others — cross-account, staff-only (the Topics Library action).
 *  Distinct from `useCopyTopic`, the workspace's same-account copy. */
export function useCopyTopicAcrossClients(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { topicId: string } & CopyTopic) => {
      const { topicId, ...body } = input
      const res = await api.api.topics.library[':topicId'].copies.$post({
        param: { topicId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to copy the topic')
      }
      return res.json()
    },
    // Copies land in other clients: refresh the library list and every workspace's topic list.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['topic-library'] })
      void queryClient.invalidateQueries({ queryKey: ['topics'] })
    },
  })
}

function invalidateFolders(queryClient: QueryClient, subaccountId: string) {
  void queryClient.invalidateQueries({ queryKey: ['folders', subaccountId] })
  return invalidateTopics(queryClient, subaccountId)
}

export function useCreateFolder(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (draft: FolderDraft) => {
      const res = await api.api.folders[':subaccountId'].$post({
        param: { subaccountId },
        json: draft,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create the folder')
      }
      return res.json()
    },
    onSuccess: () => invalidateFolders(queryClient, subaccountId),
  })
}

export function useUpdateFolder(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { folderId: string; draft: FolderDraft }) => {
      const res = await api.api.folders[':subaccountId'][':folderId'].$patch({
        param: { subaccountId, folderId: input.folderId },
        json: input.draft,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to rename the folder')
      }
      return res.json()
    },
    onSuccess: () => invalidateFolders(queryClient, subaccountId),
  })
}

export function useDeleteFolder(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (folderId: string) => {
      const res = await api.api.folders[':subaccountId'][':folderId'].$delete({
        param: { subaccountId, folderId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to delete the folder')
      }
      return res.json()
    },
    onSuccess: () => invalidateFolders(queryClient, subaccountId),
  })
}

function invalidateTags(queryClient: QueryClient, subaccountId: string) {
  void queryClient.invalidateQueries({ queryKey: ['tags', subaccountId] })
  return invalidateTopics(queryClient, subaccountId)
}

export function useCreateTag(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreateTag) => {
      const res = await api.api.tags[':subaccountId'].$post({
        param: { subaccountId },
        json: input,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create the tag')
      }
      return res.json()
    },
    onSuccess: () => invalidateTags(queryClient, subaccountId),
  })
}

export function useUpdateTag(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async ({ tagId, ...input }: UpdateTag & { tagId: string }) => {
      const res = await api.api.tags[':subaccountId'][':tagId'].$patch({
        param: { subaccountId, tagId },
        json: input,
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to update the tag')
      }
      return res.json()
    },
    onSuccess: () => invalidateTags(queryClient, subaccountId),
  })
}

export function useDeleteTag(subaccountId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (tagId: string) => {
      const res = await api.api.tags[':subaccountId'][':tagId'].$delete({
        param: { subaccountId, tagId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to delete the tag')
      }
      return res.json()
    },
    onSuccess: () => invalidateTags(queryClient, subaccountId),
  })
}
